import { AgCharts } from "ag-charts-react";
import { ChartBarIcon } from "@heroicons/react/outline";
import { AgChartOptions } from "ag-charts-community";
import { useState } from "react";

import { ChartWrapperProps } from "../types";
import { LimitBadge } from "../LimitBadge";
import { useChartOptions } from "../hooks";

import LoadingSpinner from "@/components/LoadingSpinner";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectItem,
  SelectContent,
} from "@/components/ui/select";

export const ChartWrapper = ({
  title,
  description,
  className = "",
  isLoading,
  usageType,
  processedData,
  totalOrganizationUsage,
}: ChartWrapperProps) => {
  const [chartType, setChartType] = useState(
    usageType.key === "playgroundRuns" || usageType.key === "traces" ? "periodic" : "cumulative",
  );

  const { limit, chartOptions, isOverLimit, projectedPrice, total, isEmpty } = useChartOptions({
    usageType: usageType.key,
    processedData,
    chartType: chartType as "cumulative" | "periodic",
    limit: usageType.limit,
    totalOrganizationUsage,
  });

  return (
    <div
      className={`flex h-full min-h-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md ${className}`}
    >
      <div className="mb-4 p-6 pb-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h4 className="text-lg font-semibold leading-6 text-gray-900">{title}</h4>
            <p className="text-sm leading-5 text-gray-600">{description}</p>
          </div>
          <div className="flex items-center gap-2">
            <LimitBadge
              isOverLimit={isOverLimit}
              limit={limit || 0}
              projectedPrice={projectedPrice || 0}
              isLoading={isLoading}
              total={total || 0}
            />
          </div>
        </div>
      </div>

      <div className="relative h-[280px] w-full shrink-0 px-1.5">
        <div
          className={`h-full w-full overflow-hidden rounded-lg ${
            isLoading || isEmpty ? "blur-sm" : ""
          }`}
        >
          <AgCharts
            key={`${usageType.key}-${chartType}`}
            options={chartOptions as AgChartOptions}
            className="h-full w-full"
          />
        </div>

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95">
            <div className="flex items-center gap-3">
              <LoadingSpinner size={5} />
              <span className="text-sm font-medium">Loading usage data…</span>
            </div>
          </div>
        )}

        {isEmpty && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/95">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <ChartBarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="mb-1 text-sm font-medium text-gray-700">No usage data found</div>
              <div className="text-xs text-gray-500">
                Start using the platform to see your usage data
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1" aria-hidden="true" />
      <div className="flex shrink-0 items-center justify-end px-6 pb-6">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">Type:</span>
          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger className="h-[30px] w-[150px] px-2 py-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cumulative" className="text-xs">
                Cumulative
              </SelectItem>
              <SelectItem value="periodic" className="text-xs">
                Periodic
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
